import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { Typography } from "components/common";

interface Callee {
  uuid: string;
  number: string;
  answerState: string;
}

interface CallInfo {
  destination: string;
  callerIDNumber: string;
  answerState?: string;
  isHolding?: boolean;
  isParked?: boolean;
  callees?: Callee[];
}

export enum Apps {
  IVR = "IVR",
  QUEUE = "Queue",
  EXTENSION = "Extension",
  RING_GROUP = "Ring Group"
}

interface DirectoryItem {
  extension?: string;
  name: string;
}

interface CallResult {
  infoText: JSX.Element | string;
  statusText: string;
}

const ellipsisStyle = {
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
} as const;

const InfoElement = ({ name, ext }: { name: string; ext: string }) => (
  <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
    {name} ({ext})
  </Typography>
);

const CallInfoElement = ({
  label,
  appName,
  name,
  ext,

}: {
  appName?:string
  label: string;
  name: string;
  ext: string;
}) => (
  <Typography variant="small-text-3" style={ellipsisStyle}>
    {label}{" "}
    <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
      {name} {ext} ({appName})
    </Typography>
  </Typography>
);

const FallbackElement = ({
  fallbackText,
  destination
}: {
  fallbackText: string;
  destination: string;
}) => (
  <Typography variant="small-text-3" style={ellipsisStyle}>
    {fallbackText}{" "}
    <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
      {destination}
    </Typography>
  </Typography>
);

const QueueElement = ({ name, appName, destination }: { name: string; destination: string, appName: string; }) => (
  <Typography variant="small-text-3" style={ellipsisStyle}>
    {name}{" "}
    <Typography variant="small-text-3" className="font-semibold" style={ellipsisStyle}>
      {destination} ({appName})
    </Typography>
  </Typography>
);

export const useInfoStatus = () => {
  const [infoText, setInfoText] = useState<JSX.Element | string>("");
  const [statusText, setStatusText] = useState<string>("");

  const ivrMenus = useSelector((state: RootState) => state.apps.data.ivr_menu ?? []);
  const ringGroups = useSelector((state: RootState) => state.apps.data.ring_group ?? []);
  const extensions = useSelector((state: RootState) => state.apps.data.extension ?? []);
  const callCenterData = useSelector((state: RootState) => state.apps.data.queue ?? []);

  const directoryMaps = useMemo(() => {
    const ivrMap = new Map<string, DirectoryItem>();
    const ringGroupMap = new Map<string, DirectoryItem>();
    const extMap = new Map<string, DirectoryItem>();
    const queueMap = new Map<string, DirectoryItem>();

    ivrMenus.forEach(item => {
      if (item.extension) {
        ivrMap.set(item.extension, item);
      }
    });

    ringGroups.forEach(item => {
      if (item.extension) {
        ringGroupMap.set(item.extension, item);
      }
    });

    extensions.forEach(item => {
      if (item.extension) {
        extMap.set(item.extension, item);
      }
    });

    callCenterData.forEach(item => {
      if (item.extension) {
        queueMap.set(item.extension, item);
      }
    });

    return { ivrMap, ringGroupMap, extMap, queueMap };
  }, [ivrMenus, ringGroups, extensions, callCenterData]);

  const getActionLabel = (actionType: string, answerState?: string): string => {
    switch (actionType) {
      case "hold": return "call on hold with";
      case "park": return "call on park with";
      case "talking": return answerState === "active" ? "Talking to" : `${answerState} by`;
      default: return "";
    }
  };

  const getInfoText = (info: CallInfo): JSX.Element | string => {
    const { destination, isHolding, isParked, answerState, callees } = info;
    const { ivrMap, ringGroupMap, extMap, queueMap } = directoryMaps;

    const answeredCallee = callees?.find((callee) => callee.answerState === "answered");
    
    if (answeredCallee) {
      const calleeExt = extMap.get(answeredCallee.number);
      
      if (calleeExt) {
        return <CallInfoElement appName={Apps.EXTENSION} label="answered by" name={calleeExt.name || answeredCallee.number} ext={""} />;
      }
      
      return <FallbackElement fallbackText="answered by" destination={answeredCallee.number} />;
    }

    const matchedIVR = ivrMap.get(destination);
    const matchedRing = ringGroupMap.get(destination);
    const matchedExt = extMap.get(destination);
    const matchedQueue = queueMap.get(destination);

    const action = isHolding ? "hold" : isParked ? "park" : "talking";

    if (matchedIVR) {
      const label = getActionLabel(action === "talking" ? "status" : action);
      return <CallInfoElement appName={Apps.IVR} label={label} name={matchedIVR.name} ext={destination} />;
    }

    if (matchedRing) {
      const label = getActionLabel(action === "talking" ? "status" : action);
      return <CallInfoElement appName={Apps.RING_GROUP} label={label} name={matchedRing.name} ext={destination} />;
    }

    if (matchedExt) {
      const label = getActionLabel(action, answerState);
      return <CallInfoElement appName={Apps.EXTENSION} label={label} name={matchedExt.name} ext={destination} />;
    }

    if (matchedQueue) {
      return <QueueElement appName={Apps.QUEUE} name={matchedQueue.name} destination={destination} />;
    }

    const fallbackText = isHolding || isParked
      ? `call on ${isHolding ? "hold" : "park"} with`
      : answerState === "active"
        ? "Talking to"
        : `${answerState} by`;

    return <FallbackElement fallbackText={fallbackText} destination={destination} />;
  };

  const getStatusText = (callerIDNumber: string): string => {
    const { ivrMap, ringGroupMap, extMap } = directoryMaps;

    const isOutbound = !!(
      ivrMap.get(callerIDNumber) ||
      ringGroupMap.get(callerIDNumber) ||
      extMap.get(callerIDNumber)
    );

    return isOutbound ? "Outbound" : "Inbound";
  };

  const getInfoStatus = (info: CallInfo): CallResult => {
    const resultInfoText = getInfoText(info);
    const resultStatusText = getStatusText(info.callerIDNumber);

    setInfoText(resultInfoText);
    setStatusText(resultStatusText);

    return { infoText: resultInfoText, statusText: resultStatusText };
  };

  return { getInfoStatus, getInfoText, infoText, statusText };
};
